// IM-UX-003 (IM-UI-081) — Fontes: o InteliMove procura e recomenda; a pessoa seleciona, descarta ou complementa.
// Visões: recomendadas · ativas · com problema · descartadas (· pausadas, quando houver). As contagens vêm de
// GET /sources/overview — a MESMA consulta do painel. Abrir a página só lê: a busca na web só acontece no clique.
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  SOURCE_AVAILABILITY_LABEL, SOURCE_CATEGORY_LABEL, SOURCE_CLASS_LABEL,
  type SourceDto, type SourceEvidenceDto, type SourceRecommendationCategory, type SourceRecommendationDto, type SourceSchedule, type SourcesOverviewDto, type SourceView,
} from '@intelimove/contracts';
import { useAuth } from '../auth/AuthContext';
import { Disclosure } from '../components/Guidance';
import { useSubmitOnce } from '../components/Recommendation';
import { AsyncView, EmptyState } from '../components/States';
import { useToast } from '../components/Toast';
import { FilterBar, PageHead, StatusPill } from '../components/Widgets';
import { NATURE_LABEL, SOURCE_CLASSIFICATION_LABEL, SOURCE_ORIGIN_LABEL, relativeTime, sourcesAdapter } from '../data/adapters';
import { useResource } from '../hooks/useResource';
import { SourceDiscovery } from '../components/SourceDiscovery';
import { INTEGRATION_LABEL, PAGE_TYPE_LABEL, intelApi } from '../data/intel';
import { sourceRecoApi } from '../data/sources';

const RUN_LABEL = { queued: ['Na fila', 'gray'], running: ['Coletando', 'amber'], succeeded: ['Operando', 'mint'], failed: ['Falhou', 'critical'] } as const;
const KIND_LABEL: Record<string, string> = { demo_fixture: 'Demonstrativa (conteúdo fictício)', rss: 'Feed RSS', atom: 'Feed Atom', publications_page: 'Página de publicações', page: 'Página monitorada', youtube: 'Canal do YouTube', social: 'Rede social' };
const SCHEDULE_LABEL: Record<SourceSchedule, string> = { MANUAL: 'Manual', DIARIA: 'Diária', SEMANAL: 'Semanal', QUINZENAL: 'Quinzenal', MENSAL: 'Mensal' };

function when(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function whenFull(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
}

function health(s: SourceDto): [string, string] {
  if (s.kind === 'social' && s.integrationState !== 'ATIVA') return [INTEGRATION_LABEL[s.integrationState ?? ''] ?? 'Conexão necessária', 'amber'];
  if (s.status === 'paused') return ['Pausada', 'gray'];
  if (s.requiresManualAction) return ['Precisa de atenção', 'critical'];
  if (s.lastOutcome === 'FALHA') return [`Falhou · nova tentativa ${when(s.nextRunAt)}`, 'amber'];
  const run = s.lastRun;
  if (!run) return ['Sem coleta', 'gray'];
  const [label, tone] = RUN_LABEL[run.status];
  return [label, tone];
}

function NewSourceForm({ onCreated, onCancel }: { onCreated: () => void; onCancel: () => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [kind, setKind] = useState<'feed' | 'youtube' | 'social'>('feed');
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setBusy(true);
    setError(null);
    try {
      const base = { name: String(f.get('name') ?? ''), accountName: String(f.get('accountName') ?? '').trim() || undefined };
      const url = String(f.get('url') ?? '');
      const schedule = String(f.get('schedule')) as SourceSchedule;
      const r = kind === 'youtube' ? await intelApi.createSource({ ...base, kind, reference: url, schedule })
        : kind === 'social' ? await intelApi.createSource({ ...base, kind, url })
          : await sourcesAdapter.create({ ...base, kind: 'feed', url, schedule });
      toast(kind === 'social' ? `perfil registrado. ${(r as { limitation?: string }).limitation ?? ''}` : `fonte cadastrada${'itemsFound' in r ? ` com ${r.itemsFound ?? 0} publicações encontradas` : ''}.`);
      onCreated();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="card source-form" onSubmit={submit} aria-labelledby="new-source-title">
      <div className="card-head"><div><h2 id="new-source-title">Adicionar fonte manualmente</h2><p className="card-sub">Informe o feed RSS/Atom ou a página de notícias. Se for uma página, o InteliMove procura o feed que ela declara. Endereço já monitorado não é duplicado.</p></div></div>
      {error && <div className="form-error" role="alert">{error}</div>}
      <div className="form-grid">
        <div className="field"><label htmlFor="src-name">Nome da fonte</label><input id="src-name" name="name" required minLength={2} maxLength={120} placeholder="Ex.: Blog da Empresa X" /></div>
        <div className="field"><label htmlFor="src-kind">Tipo</label><select id="src-kind" value={kind} onChange={(e) => setKind(e.target.value as 'feed')}><option value="feed">Publicações (RSS/Atom ou página de notícias)</option><option value="youtube">Canal do YouTube</option><option value="social">Perfil público em rede social</option></select></div>
        <div className="field"><label htmlFor="src-url">{kind === 'youtube' ? 'Canal (URL, @handle ou ID)' : kind === 'social' ? 'URL do perfil da empresa' : 'Endereço (feed ou página)'}</label><input id="src-url" name="url" required placeholder={kind === 'youtube' ? '@empresa' : kind === 'social' ? 'https://www.linkedin.com/company/empresa' : 'https://empresa.com.br/blog'} /></div>
        <div className="field"><label htmlFor="src-account">Empresa monitorada</label><input id="src-account" name="accountName" minLength={2} maxLength={200} placeholder="Sem empresa, as publicações não viram sinais" /></div>
        <div className="field"><label htmlFor="src-schedule">Atualização automática</label>
          <select id="src-schedule" name="schedule" defaultValue="DIARIA" disabled={kind === 'social'}><option value="DIARIA">Diária</option><option value="SEMANAL">Semanal</option><option value="QUINZENAL">Quinzenal</option><option value="MENSAL">Mensal</option><option value="MANUAL">Somente manual</option></select></div>
      </div>
      {kind === 'social' && <p className="notice-inline">Redes sociais só permitem ler publicações de terceiros com integração autorizada. O perfil fica registrado com a situação “Conexão necessária”, sem coleta — a InteliMove não faz scraping autenticado.</p>}
      <div className="form-actions"><button type="button" className="secondary" onClick={onCancel}>Cancelar</button><button className="cta" disabled={busy}>{busy ? 'Validando endereço…' : 'Cadastrar fonte'}</button></div>
    </form>
  );
}

function Publications({ source }: { source: SourceDto }) {
  const { state, reload } = useResource((s) => sourcesAdapter.evidences(source.id, s).then((r) => r.items), [source.id], (d: SourceEvidenceDto[]) => d.length === 0);
  return (
    <section className="card" aria-labelledby="pub-title" style={{ marginTop: 18 }}>
      <div className="card-head"><div><h2 id="pub-title">Publicações de {source.name}</h2><p className="card-sub">O que foi coletado, com a natureza da evidência e o motivo quando não virou sinal.</p></div></div>
      <AsyncView state={state} onRetry={reload} empty={<EmptyState title="Nenhuma publicação coletada">Execute uma coleta para ver as publicações desta fonte.</EmptyState>}>
        {(items) => (
          <ul className="pub-list">
            {items.map((e) => (
              <li key={e.id}>
                <div className="pub-main">
                  <span className="tag mint">{NATURE_LABEL[e.nature] ?? e.nature}</span>
                  {e.url ? <a href={e.url} target="_blank" rel="noopener noreferrer">{e.title}</a> : <b>{e.title}</b>}
                </div>
                <div className="pub-meta">
                  <span>{e.publishedAt ? `publicada ${relativeTime(e.publishedAt)}` : 'sem data declarada'}</span>
                  {e.signalId ? <Link to={`/sinais`}>virou sinal</Link> : <span className="muted">{e.noSignalReason ?? 'sem sinal'}</span>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </AsyncView>
    </section>
  );
}

/** IM-EXT-011: edição rápida da fonte (empresa monitorada e atualização automática), pelo PATCH existente. */
function EditSource({ source, onSaved, onCancel }: { source: SourceDto; onSaved: () => void; onCancel: () => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setBusy(true);
    try {
      const accountName = String(f.get('accountName') ?? '').trim();
      await sourcesAdapter.update(source.id, { accountName: accountName.length >= 2 ? accountName : null, schedule: String(f.get('schedule')) as SourceSchedule });
      toast(`fonte atualizada: ${source.name}.`);
      onSaved();
    } catch (err) {
      toast((err as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="card source-form" onSubmit={submit} aria-labelledby="edit-source-title">
      <div className="card-head"><div><h2 id="edit-source-title">Editar {source.name}</h2><p className="card-sub">Sem empresa monitorada, as publicações são coletadas mas não viram sinais.</p></div></div>
      <div className="form-grid">
        <div className="field"><label htmlFor="ed-account">Empresa monitorada</label><input id="ed-account" name="accountName" maxLength={200} defaultValue={source.accountName ?? ''} /></div>
        <div className="field"><label htmlFor="ed-schedule">Atualização automática</label>
          <select id="ed-schedule" name="schedule" defaultValue={source.schedule}>{Object.entries(SCHEDULE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
      </div>
      <div className="form-actions"><button type="button" className="secondary" onClick={onCancel}>Cancelar</button><button className="cta" disabled={busy}>{busy ? 'Salvando…' : 'Salvar'}</button></div>
    </form>
  );
}

const RELIABILITY: Record<string, [string, string]> = { ALTA: ['alta', 'mint'], MEDIA: ['média', 'amber'], BAIXA: ['baixa', 'gray'] };
const RECO_KIND: Record<string, string> = { rss: 'Feed', atom: 'Feed', page: 'Página', youtube: 'YouTube', social: 'Rede social' };
const CATEGORY_ORDER: Array<SourceRecommendationCategory | 'OUTRAS'> = ['CONCORRENTE', 'EMPRESA', 'OFICIAL', 'MERCADO', 'ICP_SEGMENTO', 'OUTRAS'];
const RELATED_TYPE: Record<string, string> = { EMPRESA: 'Empresa', CONCORRENTE: 'Concorrente', EMPRESA_ALVO: 'Empresa-alvo', SEGMENTO: 'Segmento', ANALISE: 'Análises', MERCADO: 'Mercado' };
const VIEWS: SourceView[] = ['recomendadas', 'ativas', 'problemas', 'descartadas', 'pausadas'];
const isView = (v: string | null): v is SourceView => !!v && (VIEWS as string[]).includes(v);
const safeHref = (u: string | null | undefined) => (u && /^https?:\/\//i.test(u) ? u : null);

/** Cartão de uma recomendação: o que é, por que, confiabilidade, verificação, periodicidade e ações. */
function RecommendationCard({ r, mode, canAct, selected, onSelect, onActivate, onDismiss, onRestore, onSchedule, busy }: {
  r: SourceRecommendationDto; mode: 'review' | 'problem' | 'dismissed'; canAct: boolean; selected: boolean; busy: boolean;
  onSelect: (on: boolean) => void; onActivate: () => void; onDismiss: () => void; onRestore: () => void; onSchedule: (s: SourceSchedule) => void;
}) {
  const [rel, relTone] = RELIABILITY[r.reliability] ?? ['—', 'gray'];
  const href = safeHref(r.url);
  const cbId = `sr-${r.id}`;
  return (
    <li className={`sr-card${selected ? ' selected' : ''}`}>
      <div className="sr-head">
        {mode === 'review' && canAct && <input id={cbId} type="checkbox" className="sr-check" checked={selected} onChange={(e) => onSelect(e.target.checked)} aria-label={`Selecionar ${r.name}`} />}
        <div className="sr-title">
          <label htmlFor={mode === 'review' && canAct ? cbId : undefined}><b>{r.name}</b></label>
          <span className="cell-sub break">{r.url}</span>
        </div>
        <div className="sr-tags">
          <span className="tag mint">{RECO_KIND[r.kind] ?? r.kind}{r.pageType && r.kind === 'page' ? ` · ${PAGE_TYPE_LABEL[r.pageType] ?? r.pageType}` : ''}</span>
          <span className="tag petrol">{SOURCE_CLASS_LABEL[r.sourceClass]}</span>
        </div>
      </div>
      <p className="sr-reason">{r.reason}</p>
      <dl className="sr-meta">
        {r.related && <div><dt>Relacionada a</dt><dd>{RELATED_TYPE[r.related.type] ?? 'Relacionada'}: {r.related.name}</dd></div>}
        <div><dt>Confiabilidade</dt><dd><StatusPill tone={relTone}>{rel}</StatusPill></dd></div>
        <div><dt>Verificação</dt><dd>{SOURCE_AVAILABILITY_LABEL[r.availability]}{r.lastCheckedAt ? ` · ${relativeTime(r.lastCheckedAt)}` : ''}</dd></div>
        <div><dt>Periodicidade</dt><dd>{mode === 'review' && canAct && r.kind !== 'social'
          ? <select aria-label={`Periodicidade: ${r.name}`} value={r.suggestedSchedule} onChange={(e) => onSchedule(e.target.value as SourceSchedule)}>{Object.entries(SCHEDULE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
          : r.kind === 'social' ? 'Manual (exige conexão autorizada)' : SCHEDULE_LABEL[r.suggestedSchedule]}</dd></div>
      </dl>
      <div className="sr-actions">
        {mode === 'review' && canAct && <button className="small-action" disabled={busy} onClick={onActivate} aria-label={`Ativar: ${r.name}`}>Ativar</button>}
        {mode !== 'dismissed' && canAct && <button className="small-action ghost" disabled={busy} onClick={onDismiss} aria-label={`Descartar: ${r.name}`}>Descartar</button>}
        {mode === 'dismissed' && canAct && <button className="small-action" disabled={busy} onClick={onRestore} aria-label={`Restaurar: ${r.name}`}>Restaurar</button>}
        {href && <a className="small-action ghost" href={href} target="_blank" rel="noopener noreferrer nofollow" aria-label={`Abrir fonte: ${r.name} (abre em nova aba)`}>Abrir fonte</a>}
      </div>
      <Disclosure className="sr-tech" title="Detalhes técnicos">
        <ul className="plain-list">
          <li>Origem: {r.origin === 'RECOMENDACAO' ? 'recomendada pelo InteliMove a partir do contexto' : 'descoberta no site informado'} ({r.method})</li>
          {r.category && <li>Categoria: {SOURCE_CATEGORY_LABEL[r.category]}</li>}
          {r.feedUrl && r.feedUrl !== r.url && <li className="break">Feed lido: {r.feedUrl}</li>}
          {r.originalUrl && <li className="break">Endereço encontrado: {r.originalUrl} (redirecionado)</li>}
          {r.checkDetail && <li>Última verificação: {r.checkDetail}</li>}
          {r.kind === 'social' && <li>Redes sociais não são lidas sem conexão autorizada: a fonte fica registrada, sem coleta.</li>}
        </ul>
      </Disclosure>
    </li>
  );
}

/** Tabela das fontes monitoradas (ativas, pausadas ou com problema). */
function MonitoredTable({ rows, canRun, busy, selected, editing, onCollect, onStatus, onSelect, onEdit }: {
  rows: SourceDto[]; canRun: boolean; busy: string | null; selected: SourceDto | null; editing: SourceDto | null;
  onCollect: (s: SourceDto) => void; onStatus: (s: SourceDto, st: 'active' | 'paused') => void; onSelect: (s: SourceDto | null) => void; onEdit: (s: SourceDto | null) => void;
}) {
  return (
    <div className="table-card"><div className="table-scroll">
      <table className="data-table stack">
        <caption className="sr-only">Fontes monitoradas da organização</caption>
        <thead><tr><th scope="col">Fonte</th><th scope="col">Tipo</th><th scope="col">Coletas</th><th scope="col">Produção</th><th scope="col">Situação</th><th scope="col"><span className="sr-only">Ações</span></th></tr></thead>
        <tbody>
          {rows.map((s) => {
            const [label, tone] = health(s);
            const real = (s.classification ?? 'REAL') === 'REAL';
            const related = s.targetName ?? s.accountName ?? s.relatedCompetitor;
            const href = safeHref(s.url ?? s.feedUrl);
            return (
              <tr key={s.id}>
                <td data-label="Fonte"><b>{s.name}</b>{!real && <> <StatusPill tone="critical">{SOURCE_CLASSIFICATION_LABEL[s.classification!]}</StatusPill></>}
                  {related && <span className="cell-sub">{s.relatedCompetitor && !s.accountName ? 'Concorrente' : 'Empresa'}: {related}</span>}
                  <span className="cell-sub">{s.origin ? SOURCE_ORIGIN_LABEL[s.origin] : 'Origem não registrada'} · classificação {(SOURCE_CLASSIFICATION_LABEL[s.classification ?? 'REAL'] ?? 'real').toLowerCase()}</span>
                  {(s.url ?? s.feedUrl) && <span className="cell-sub break">{s.url ?? s.feedUrl}</span>}</td>
                <td data-label="Tipo">{KIND_LABEL[s.kind] ?? s.kind}{s.kind === 'page' && s.pageType ? ` · ${PAGE_TYPE_LABEL[s.pageType] ?? s.pageType}` : ''}<span className="cell-sub nowrap">{SCHEDULE_LABEL[s.schedule]}</span></td>
                <td data-label="Coletas"><span className="cell-sub">Última: {s.lastRunAt ? relativeTime(s.lastRunAt) : 'nunca'}</span><span className="cell-sub">Último sucesso: {whenFull(s.lastSuccessAt ?? null)}</span>
                  <span className="cell-sub">Próxima: {s.schedule !== 'MANUAL' && s.status === 'active' && s.nextRunAt ? when(s.nextRunAt) : s.schedule === 'MANUAL' ? 'manual' : '—'}</span></td>
                <td data-label="Produção" className="prob">{s.signalCount} sinais<span className="cell-sub">Última publicação: {whenFull(s.lastPublicationAt ?? null)}</span></td>
                <td data-label="Situação"><StatusPill tone={tone}>{label}</StatusPill><span className="cell-sub">{s.failedRuns ?? 0} coleta(s) com falha</span>{s.lastError && <span className="cell-sub" title={s.lastError}>{s.lastError.slice(0, 80)}</span>}</td>
                <td className="row-actions">
                  {href && <a className="small-action" href={href} target="_blank" rel="noopener noreferrer" aria-label={`Abrir fonte: ${s.name} (abre em nova aba)`}>Abrir fonte</a>}
                  <button className="small-action" onClick={() => onSelect(selected?.id === s.id ? null : s)} aria-expanded={selected?.id === s.id} aria-label={`Ver publicações: ${s.name}`}>Publicações</button>
                  {canRun && real && <button className="small-action" disabled={busy !== null} onClick={() => onCollect(s)} aria-label={`Testar agora: ${s.name}`} title="Executa uma coleta agora e mostra o resultado">{busy === s.id ? 'Coletando…' : 'Testar agora'}</button>}
                  {canRun && real && (s.status === 'paused' || s.requiresManualAction
                    ? <button className="small-action" onClick={() => onStatus(s, 'active')} aria-label={`Ativar: ${s.name}`}>Ativar</button>
                    : <button className="small-action" onClick={() => onStatus(s, 'paused')} aria-label={`Pausar: ${s.name}`}>Pausar</button>)}
                  {canRun && real && <button className="small-action" onClick={() => onEdit(editing?.id === s.id ? null : s)} aria-label={`Editar: ${s.name}`}>Editar</button>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div></div>
  );
}

function runLine(ov: SourcesOverviewDto): string | null {
  const r = ov.latestRun;
  if (!r) return null;
  if (r.status === 'QUEUED' || r.status === 'RUNNING') return 'Buscando fontes no site da sua empresa, dos concorrentes e em fontes oficiais…';
  if (r.status === 'FAILED') return `A última busca não terminou (${r.error ?? 'falha'}). Nada foi repetido automaticamente.`;
  const s = r.stats;
  return `Última busca ${r.finishedAt ? relativeTime(r.finishedAt) : ''}: ${s.created ?? 0} nova(s), ${s.updated ?? 0} reverificada(s), ${s.alreadyActive ?? 0} já monitorada(s)${r.status === 'PARTIAL' ? ' · concluída em parte' : ''}.`;
}

/** Visão "Recomendadas": busca, seleção, ativação em lote, cartões por categoria e estados vazios. */
function Recommended({ ov, canAct, onChanged, onAdd }: { ov: SourcesOverviewDto; canAct: boolean; onChanged: () => void; onAdd: () => void }) {
  const toast = useToast();
  const [, setParams] = useSearchParams();
  const { state, reload, refresh } = useResource((s) => sourceRecoApi.list('recomendadas', s).then((r) => r.items), [], (d: SourceRecommendationDto[]) => d.length === 0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [customizing, setCustomizing] = useState(false);
  const { busy, run } = useSubmitOnce();
  const running = ov.latestRun?.status === 'QUEUED' || ov.latestRun?.status === 'RUNNING';
  const prevRunning = useRef(running);
  useEffect(() => { if (prevRunning.current && !running) reload(); prevRunning.current = running; }, [running, reload]);

  const search = (force: boolean) => run(async () => {
    try {
      const r = await sourceRecoApi.discover(force);
      if (r.reused === 'RECENT') toast('as recomendações foram atualizadas há pouco; mostrando o resultado recente. Use “Buscar novamente” para repetir.');
      else toast(r.reused === 'RUNNING' ? 'a busca já está em andamento.' : 'busca iniciada. As recomendações aparecem aqui quando terminar.');
      onChanged();
    } catch (e) { toast((e as Error).message, 'error'); }
  });
  const activate = (items: SourceRecommendationDto[]) => run(async () => {
    try {
      const r = await sourceRecoApi.activate(items.map((i) => ({ id: i.id, schedule: i.suggestedSchedule })));
      const failed = r.results.filter((x) => x.outcome === 'FAILED');
      const dup = r.results.filter((x) => x.outcome === 'ALREADY_ACTIVE').length;
      toast(`${r.activated} fonte(s) ativada(s)${dup ? `, ${dup} já monitorada(s)` : ''}${failed.length ? `, ${failed.length} com problema: ${failed[0]!.message ?? ''}` : ''}.`, failed.length && !r.activated ? 'error' : undefined);
      setSelected(new Set());
      reload(); onChanged();
    } catch (e) { toast((e as Error).message, 'error'); }
  });
  const dismiss = (r: SourceRecommendationDto) => run(async () => {
    try { await sourceRecoApi.dismiss(r.id); toast(`recomendação descartada: ${r.name}. Você pode restaurá-la em “Descartadas”.`); setSelected((s) => { const n = new Set(s); n.delete(r.id); return n; }); refresh(); onChanged(); }
    catch (e) { toast((e as Error).message, 'error'); }
  });
  const setSchedule = async (r: SourceRecommendationDto, schedule: SourceSchedule) => {
    try { await sourceRecoApi.setSchedule(r.id, schedule); refresh(); } catch (e) { toast((e as Error).message, 'error'); }
  };

  const items = state.kind === 'ready' ? state.data : [];
  const groups = useMemo(() => CATEGORY_ORDER.map((cat) => [cat, items.filter((i) => (i.category ?? 'OUTRAS') === cat)] as const).filter(([, xs]) => xs.length), [items]);
  const chosen = items.filter((i) => selected.has(i.id));
  const reviewed = ov.activated + ov.dismissed > 0;

  const empty = !ov.latestRun && !reviewed
    ? <EmptyState title="Ainda não buscamos fontes para sua empresa." action={canAct ? <button className="cta" disabled={busy} onClick={() => search(false)}>Buscar fontes recomendadas</button> : undefined}>
        O InteliMove usa o site, os concorrentes, os ICPs e as análises que você já registrou. Nada é ativado sem a sua decisão.</EmptyState>
    : running ? <EmptyState title="Buscando fontes recomendadas…">Isso leva de alguns segundos a poucos minutos. Você pode sair desta tela.</EmptyState>
      : reviewed
        ? <EmptyState title="Todas as recomendações foram revisadas.">As ativadas estão em <button className="link-btn" onClick={() => setParams({ visao: 'ativas' })}>Ativas</button>. Busque novamente quando o contexto mudar.</EmptyState>
        : <EmptyState title="Não encontramos fontes suficientes com o contexto atual." action={<span className="sr-empty-actions"><Link className="secondary btn-link" to="/configuracoes/contexto">Revisar contexto</Link>{canAct && <button className="secondary" onClick={() => setCustomizing(true)}>Personalizar descoberta</button>}{canAct && <button className="secondary" onClick={onAdd}>Adicionar fonte manualmente</button>}</span>}>
            Cadastre o site da empresa e confirme concorrentes com site para ampliar a busca.</EmptyState>;

  return (
    <>
      <section className="card sr-intro" aria-labelledby="sr-title">
        <div className="sr-intro-head">
          <div>
            <h2 id="sr-title">Fontes recomendadas para sua empresa</h2>
            <p className="card-sub">O InteliMove utiliza sua empresa, mercado, segmentos, concorrentes e análises para localizar fontes públicas relevantes.</p>
          </div>
          {canAct && (
            <div className="head-actions">
              {ov.latestRun ? <button className="secondary" disabled={busy || running} onClick={() => search(true)}>{running ? 'Buscando…' : 'Buscar novamente'}</button>
                : <button className="cta" disabled={busy || running} onClick={() => search(false)}>Buscar fontes recomendadas</button>}
              <button className="cta" disabled={busy || chosen.length === 0} onClick={() => activate(chosen)}>Ativar selecionadas{chosen.length ? ` (${chosen.length})` : ''}</button>
            </div>
          )}
        </div>
        {runLine(ov) && <p className={`sr-run${running ? ' live' : ''}`} role="status">{runLine(ov)}</p>}
        <div className="sr-secondary">
          {canAct && <button className="link-btn" onClick={onAdd}>Adicionar fonte manualmente</button>}
          {ov.dismissed > 0 && <button className="link-btn" onClick={() => setParams({ visao: 'descartadas' })}>Revisar descartadas ({ov.dismissed})</button>}
          {canAct && <button className="link-btn" aria-expanded={customizing} onClick={() => setCustomizing((x) => !x)}>Personalizar descoberta</button>}
        </div>
        {ov.latestRun && (ov.latestRun.notes.length > 0 || !ov.canDiscover.companySite) && (
          <Disclosure title="O que a última busca considerou" hint={`${ov.latestRun.stats.checked ?? 0} verificação(ões)`}>
            <ul className="plain-list">
              <li>Site da empresa: {ov.canDiscover.companySite ?? 'não cadastrado no contexto'} · concorrentes com site: {ov.canDiscover.competitorsWithSite} · empresas-alvo com site: {ov.canDiscover.targetsWithSite}</li>
              {ov.latestRun.notes.map((n) => <li key={n}>{n}</li>)}
            </ul>
          </Disclosure>
        )}
        {customizing && canAct && (
          <div className="sr-custom">
            <p className="card-sub">Descobrir fontes em outro site (ex.: um parceiro ou uma publicação do setor). O resultado entra nesta lista para revisão.</p>
            <SourceDiscovery onChanged={() => { reload(); onChanged(); }} />
          </div>
        )}
      </section>
      <AsyncView state={state} onRetry={reload} empty={empty}>
        {() => (
          <>
            {canAct && <div className="sr-bulk">
              <label className="sr-all"><input type="checkbox" checked={chosen.length === items.length && items.length > 0} onChange={(e) => setSelected(e.target.checked ? new Set(items.map((i) => i.id)) : new Set())} /> Selecionar todas ({items.length})</label>
              <span className="cell-sub">Nenhuma fonte é ativada sem a sua decisão. Redes sociais ficam registradas sem coleta.</span>
            </div>}
            {groups.map(([cat, xs]) => (
              <section key={cat} className="sr-group" aria-labelledby={`sr-g-${cat}`}>
                <h3 id={`sr-g-${cat}`} className="sr-group-title">{cat === 'OUTRAS' ? 'Descobertas em sites informados' : SOURCE_CATEGORY_LABEL[cat]} <span className="cell-sub inline">({xs.length})</span></h3>
                <ul className="sr-list">
                  {xs.map((r) => (
                    <RecommendationCard key={r.id} r={r} mode="review" canAct={canAct} busy={busy} selected={selected.has(r.id)}
                      onSelect={(on) => setSelected((s) => { const n = new Set(s); if (on) n.add(r.id); else n.delete(r.id); return n; })}
                      onActivate={() => activate([r])} onDismiss={() => dismiss(r)} onRestore={() => undefined} onSchedule={(sc) => setSchedule(r, sc)} />
                  ))}
                </ul>
              </section>
            ))}
          </>
        )}
      </AsyncView>
    </>
  );
}

/** Recomendações descartadas (restaurar) ou indisponíveis na última verificação (descartar). */
function RecommendationList({ view, canAct, onChanged }: { view: 'descartadas' | 'problemas'; canAct: boolean; onChanged: () => void }) {
  const toast = useToast();
  const { state, reload } = useResource((s) => sourceRecoApi.list(view, s).then((r) => r.items), [view], (d: SourceRecommendationDto[]) => d.length === 0);
  const { busy, run } = useSubmitOnce();
  const act = (r: SourceRecommendationDto, what: 'restore' | 'dismiss') => run(async () => {
    try {
      await (what === 'restore' ? sourceRecoApi.restore(r.id) : sourceRecoApi.dismiss(r.id));
      toast(what === 'restore' ? `recomendação restaurada: ${r.name}.` : `recomendação descartada: ${r.name}.`);
      reload(); onChanged();
    } catch (e) { toast((e as Error).message, 'error'); }
  });
  const empty = view === 'descartadas' ? <EmptyState title="Nenhuma recomendação descartada.">O que você descartar aparece aqui e pode ser restaurado.</EmptyState> : null;
  return (
    <AsyncView state={state} onRetry={reload} empty={empty}>
      {(items) => (
        <section className="sr-group" aria-labelledby={`sr-v-${view}`}>
          <h3 id={`sr-v-${view}`} className="sr-group-title">{view === 'descartadas' ? 'Recomendações descartadas' : 'Recomendações indisponíveis na última verificação'} <span className="cell-sub inline">({items.length})</span></h3>
          <ul className="sr-list">
            {items.map((r) => <RecommendationCard key={r.id} r={r} mode={view === 'descartadas' ? 'dismissed' : 'problem'} canAct={canAct} busy={busy} selected={false}
              onSelect={() => undefined} onActivate={() => undefined} onDismiss={() => act(r, 'dismiss')} onRestore={() => act(r, 'restore')} onSchedule={() => undefined} />)}
          </ul>
        </section>
      )}
    </AsyncView>
  );
}

/** Fontes monitoradas de uma visão (ativas, pausadas, com problema), com publicações, coleta, pausa e edição. */
function Monitored({ view, canRun, onChanged, fonteParam }: { view: 'ativas' | 'pausadas' | 'problemas' | 'all'; canRun: boolean; onChanged: () => void; fonteParam: string | null }) {
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const [selected, setSelected] = useState<SourceDto | null>(null);
  const [editing, setEditing] = useState<SourceDto | null>(null);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  const { state, reload } = useResource((s) => sourceRecoApi.monitored(view, s).then((r) => r.items), [view], (d: SourceDto[]) => d.length === 0);
  useEffect(() => {
    if (fonteParam && state.kind === 'ready' && !selected) setSelected(state.data.find((x) => x.id === fonteParam) ?? null);
  }, [fonteParam, state, selected]);
  const changed = useCallback(() => { reload(); onChanged(); }, [reload, onChanged]);

  // Dispara a coleta (fila própria) e acompanha o estado da execução até um estado terminal.
  const collect = async (src: SourceDto) => {
    setBusy(src.id);
    try {
      let r = await sourcesAdapter.run(src.id);
      for (let i = 0; i < 45 && alive.current && (r.status === 'queued' || r.status === 'running'); i++) {
        await new Promise((res) => setTimeout(res, 1000));
        r = await sourcesAdapter.getRun(r.id);
      }
      if (r.status === 'succeeded') toast(`coleta concluída: ${r.itemsSeen} itens lidos, ${r.evidencesCreated} novas evidências, ${r.signalsCreated} novos sinais.`);
      else if (r.status === 'failed') toast(`a coleta falhou: ${r.error ?? 'erro desconhecido'}`, 'error');
      else toast('coleta em andamento; o resultado aparecerá em instantes.');
      changed();
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      if (alive.current) setBusy(null);
    }
  };
  const setStatus = async (src: SourceDto, status: 'active' | 'paused') => {
    try {
      await sourcesAdapter.update(src.id, { status });
      toast(status === 'paused' ? `fonte pausada: ${src.name}.` : `fonte ativada: ${src.name}.`);
      changed();
    } catch (e) {
      toast((e as Error).message, 'error');
    }
  };
  const empty = view === 'ativas'
    ? <EmptyState title="Nenhuma fonte ativa ainda.">Ative as recomendadas ou adicione uma fonte manualmente.</EmptyState>
    : view === 'problemas' ? <EmptyState title="Nenhuma fonte com problema.">As coletas recentes terminaram sem falha.</EmptyState>
      : <EmptyState title="Nenhuma fonte pausada." />;
  return (
    <>
      <AsyncView state={state} onRetry={reload} empty={empty}>
        {(rows) => <MonitoredTable rows={rows} canRun={canRun} busy={busy} selected={selected} editing={editing} onCollect={collect} onStatus={setStatus} onSelect={setSelected} onEdit={setEditing} />}
      </AsyncView>
      {editing && <EditSource source={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); changed(); }} />}
      {selected && <Publications source={selected} />}
    </>
  );
}

export function Sources() {
  const { me } = useAuth();
  const canRun = me?.activeOrganization?.role === 'OWNER' || me?.activeOrganization?.role === 'ADMIN';
  const [params, setParams] = useSearchParams();
  // IM-EXT-011: `?fonte=<id>` (vindo do detalhe de um sinal) abre as publicações daquela fonte.
  const fonteParam = params.get('fonte');
  const [adding, setAdding] = useState(false);
  const ov = useResource((s) => sourceRecoApi.overview(s), []);
  const o = ov.state.kind === 'ready' ? ov.state.data : null;
  const running = o?.latestRun?.status === 'QUEUED' || o?.latestRun?.status === 'RUNNING';
  // Acompanha a busca em segundo plano sem recarregar a tela (revalidação silenciosa).
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => ov.refresh(), 2500);
    return () => clearInterval(t);
  }, [running, ov.refresh]);
  const param = params.get('visao');
  const view: SourceView = isView(param) ? param : fonteParam ? 'ativas' : o && o.recommended === 0 && o.active + o.paused + o.failing > 0 ? 'ativas' : 'recomendadas';
  const go = (v: SourceView) => setParams({ visao: v });
  const options = [
    { value: 'recomendadas' as SourceView, label: `Recomendadas${o ? ` (${o.recommended})` : ''}` },
    { value: 'ativas' as SourceView, label: `Ativas${o ? ` (${o.active})` : ''}` },
    { value: 'problemas' as SourceView, label: `Com problema${o ? ` (${o.problems})` : ''}` },
    { value: 'descartadas' as SourceView, label: `Descartadas${o ? ` (${o.dismissed})` : ''}` },
    ...(o && (o.paused > 0 || view === 'pausadas') ? [{ value: 'pausadas' as SourceView, label: `Pausadas (${o.paused})` }] : []),
  ];
  const changed = ov.refresh;
  return (
    <section aria-labelledby="page-title">
      <PageHead eyebrow="Origem dos sinais" title="Fontes" subtitle="O InteliMove procura e recomenda; você decide o que monitorar."
        actions={<button className="secondary" disabled={!canRun || adding} title={canRun ? undefined : 'Requer papel de administrador'} onClick={() => setAdding(true)}>Adicionar fonte manualmente</button>} />
      {adding && <NewSourceForm onCancel={() => setAdding(false)} onCreated={() => { setAdding(false); changed(); go('ativas'); }} />}
      <FilterBar label="Visão das fontes" value={view} onChange={go} options={options} />
      {!o && ov.state.kind === 'loading' ? null : view === 'recomendadas' && o ? <Recommended ov={o} canAct={canRun} onChanged={changed} onAdd={() => setAdding(true)} />
        : view === 'descartadas' ? <RecommendationList view="descartadas" canAct={canRun} onChanged={changed} />
          : view === 'problemas' ? <>
              <Monitored view="problemas" canRun={canRun} onChanged={changed} fonteParam={fonteParam} />
              {o && o.unavailableRecommendations > 0 && <RecommendationList view="problemas" canAct={canRun} onChanged={changed} />}
            </>
            : view === 'pausadas' ? <Monitored view="pausadas" canRun={canRun} onChanged={changed} fonteParam={fonteParam} />
              : <Monitored view={fonteParam && !isView(param) ? 'all' : 'ativas'} canRun={canRun} onChanged={changed} fonteParam={fonteParam} />}
      {view !== 'recomendadas' && <p className="cell-sub">Comparação de versões de páginas fica em <Link to="/monitoramento">Monitoramento</Link>.</p>}
    </section>
  );
}
